import React from "react";
import PropTypes from "prop-types";

import "./ChartFrame.scss";
import { Skeleton } from "../ds";

/**
 * ChartSkeleton — reserved-height placeholder for a chart that has not loaded.
 *
 * Sibling to ChartEmptyState ("no data") — this is the *loading* state. It
 * occupies the chart variant's final height inside a `cb-chart-frame` so the
 * region does not resize when the real chart mounts in its place.
 *
 * Heights mirror the `max-height` caps in ChartFrame.scss per variant.
 */
const VARIANT_HEIGHT = {
  line: 280,
  radar: 360,
  bars: 520,
  stack: 340,
};

export default function ChartSkeleton({ variant = "line", height, legend = true }) {
  const frameHeight = height != null ? height : VARIANT_HEIGHT[variant] || 280;

  return (
    <div
      className={`cb-chart-frame cb-chart-frame--${variant} cb-chart-frame--loading`}
      role="status"
      aria-busy="true"
      aria-label="Loading chart"
    >
      <Skeleton
        className="cb-chart-frame__skeleton"
        width="100%"
        height={frameHeight}
        radius="var(--cb-radius-lg)"
      />
      {legend && (
        <div className="cb-chart-frame__legend cb-chart-frame__legend--skeleton">
          {[64, 88, 72].map((w, i) => (
            <span key={i} className="cb-chart-frame__legend-item">
              <Skeleton width={w} height={12} radius="var(--cb-radius-sm)" />
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

ChartSkeleton.propTypes = {
  variant: PropTypes.oneOf(["line", "radar", "bars", "stack"]),
  /** Override the variant's default reserved height (px or CSS length). */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  legend: PropTypes.bool,
};
